/**
 * Lead source / UTM capture helpers.
 *
 * Captures attribution params on first visit (URL search params + document.referrer),
 * persists them to localStorage so they survive across navigations and the auth
 * redirect, and re-derives the channel bucket so the LeadSourceTracker can short-
 * circuit a noop POST if nothing changed.
 *
 * The backend re-derives channel server-side as well (defense in depth) — these
 * utilities exist so we know whether a meaningful new touch happened before
 * sending an /api/attribution write.
 *
 * CONSENT: marketing attribution is non-essential under ePrivacy/GDPR. Capture is gated
 * on the shared `marketing` consent (`vib3_consent` cookie on `.vib3.tech`, read via
 * @vib3/consent). Without consent we persist nothing and clear any prior touch.
 */

import { hasConsent, readClient } from '@vib3/consent';

const STORAGE_KEY = 'lead_attribution';

export interface AttributionPayload {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  referrer: string | null;
  landing_page: string | null;
  channel: string;
  seen_at: string;
}

const PAID_SEARCH_MEDIA = new Set(['cpc', 'ppc', 'paidsearch', 'paid_search', 'search-paid']);
const PAID_SOCIAL_MEDIA = new Set(['paid_social', 'paidsocial', 'social-paid', 'cpm-social', 'cpm']);
const SEARCH_HOST_FRAGMENTS = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'yandex.'];
const SOCIAL_HOST_FRAGMENTS = [
  'facebook.com', 'twitter.com', 'x.com', 'linkedin.com', 'instagram.com',
  'tiktok.com', 'reddit.com', 'youtube.com', 'pinterest.com',
];

export function deriveChannel(input: {
  utm_medium: string | null;
  utm_source: string | null;
  gclid: string | null;
  fbclid: string | null;
  referrer: string | null;
}): string {
  const medium = (input.utm_medium ?? '').toLowerCase().trim();
  const source = (input.utm_source ?? '').toLowerCase().trim();
  const ref = (input.referrer ?? '').toLowerCase();

  if (input.gclid) return 'paid_search';
  if (input.fbclid) return 'paid_social';
  if (PAID_SEARCH_MEDIA.has(medium)) return 'paid_search';
  if (PAID_SOCIAL_MEDIA.has(medium)) return 'paid_social';
  if (medium === 'email') return 'email';
  if (medium === 'referral') return 'referral';
  if (medium === 'organic') return 'organic';
  if (medium === 'social') return 'organic_social';

  if (ref) {
    if (SEARCH_HOST_FRAGMENTS.some((h) => ref.includes(h))) return 'organic';
    if (SOCIAL_HOST_FRAGMENTS.some((h) => ref.includes(h))) return 'organic_social';
    return 'referral';
  }

  if (source || medium) return medium || source;
  return 'direct';
}

/**
 * Read URL search params + document.referrer. Returns null if there's no
 * meaningful signal (no UTM params, no click ID, no referrer) so the caller
 * can skip writing a noop "direct" touch every time.
 */
export function parseAttributionFromUrl(): AttributionPayload | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => params.get(k) || null;

  const utm_source = get('utm_source');
  const utm_medium = get('utm_medium');
  const utm_campaign = get('utm_campaign');
  const utm_term = get('utm_term');
  const utm_content = get('utm_content');
  const gclid = get('gclid');
  const fbclid = get('fbclid');
  const referrer = (typeof document !== 'undefined' && document.referrer) || null;

  const hasSignal = !!(
    utm_source || utm_medium || utm_campaign || utm_term || utm_content ||
    gclid || fbclid || referrer
  );
  if (!hasSignal) return null;

  return {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_term,
    utm_content,
    gclid,
    fbclid,
    referrer,
    landing_page: window.location.pathname + window.location.search,
    channel: deriveChannel({ utm_medium, utm_source, gclid, fbclid, referrer }),
    seen_at: new Date().toISOString(),
  };
}

export function loadStoredAttribution(): AttributionPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AttributionPayload;
  } catch {
    return null;
  }
}

export function storeAttribution(p: AttributionPayload): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota errors */
  }
}

/** Remove any stored attribution. Called when marketing consent is absent/withdrawn so we
 *  don't retain non-consented data. */
export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Last-non-direct wins: a paid touch (paid_search/paid_social/email/referral)
 * upgrades over an existing direct/organic touch. Two paid touches: keep the
 * earlier one (don't churn attribution credit on every visit).
 */
export function shouldOverwriteStored(
  prev: AttributionPayload | null,
  next: AttributionPayload,
): boolean {
  if (!prev) return true;
  const PAID = new Set(['paid_search', 'paid_social', 'email', 'referral']);
  const prevPaid = PAID.has(prev.channel);
  const nextPaid = PAID.has(next.channel);
  if (!prevPaid && nextPaid) return true;
  return false;
}

/**
 * Capture-on-mount: parse URL → store if first touch or upgrade.
 * Returns the payload that should be sent to the backend (always the latest
 * stored value once we've decided), or null if nothing's worth sending.
 */
export function captureCurrentTouch(): AttributionPayload | null {
  // Gate on marketing consent (shared `vib3_consent` cookie). Default-denied when the
  // cookie is absent (e.g. not on a *.vib3.tech origin) — capture nothing and drop any
  // previously stored touch.
  if (!hasConsent(readClient(), 'marketing')) {
    clearStoredAttribution();
    return null;
  }
  const parsed = parseAttributionFromUrl();
  const stored = loadStoredAttribution();
  if (parsed && shouldOverwriteStored(stored, parsed)) {
    storeAttribution(parsed);
    return parsed;
  }
  return stored;
}
